import React from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import { LegalLayout } from './LegalLayout';
import { usePlans } from '../hooks/useBilling';
import { money, trialCta, trialLength } from '../lib/billing';

// Same four lines as the paywall (Subscription.tsx) and mobile.
const FEATURES = [
  'Unlimited scans, every page',
  'Transcription with titles, tags & summaries',
  'Ask anything across all your notebooks',
  'Collections, search & sharing',
] as const;

/** Public pricing page. Amounts and trial length come from Paddle via `/api/payments/plans`. */
export const Pricing: React.FC = () => {
  const { plan, isLoading } = usePlans();
  const trial = plan ? trialLength(plan.trial_days) : null;
  const per = plan ? (plan.interval_count > 1 ? `every ${plan.interval_count} ${plan.interval}s` : plan.interval) : '';

  return (
    <LegalLayout
      kicker="Pricing"
      title="One plan. Everything included."
      lastUpdated="October 2, 2026"
      documentTitle="Pricing — PaperBrain"
      intro="No credits to count and no tiers to compare. Scan as much as you like, cancel anytime."
    >
      <div className="pb-card my-8 px-6 py-6">
        {plan ? (
          <>
            <p className="m-0 font-body text-[0.8125rem] font-semibold uppercase tracking-wider text-accent">
              PaperBrain
            </p>
            <p className="m-0 mt-2 flex items-baseline gap-2">
              <span className="pb-heading-1 text-[3rem] tracking-[-0.02em]">{money(plan)}</span>
              <span className="font-body text-base text-on-surface-variant">/ {per}</span>
            </p>
            <p className="m-0 mt-1 font-body text-[0.875rem] text-on-surface-variant">
              Tax included. Billed monthly{trial ? ` after your free ${plan.trial_days}-day trial` : ''}.
            </p>

            <div className="mt-5">
              {FEATURES.map((f, i) => (
                <div
                  key={f}
                  className={`flex items-center gap-3 py-3 ${i > 0 ? 'border-0 border-t border-solid border-outline-variant' : ''}`}
                >
                  <span className="flex h-[21px] w-[21px] shrink-0 items-center justify-center rounded-full bg-surface-highlight text-accent">
                    <Check size={13} strokeWidth={2.6} />
                  </span>
                  <span className="font-body text-[0.9375rem] text-on-surface">{f}</span>
                </div>
              ))}
            </div>

            <Link to="/register" className="pb-btn pb-btn--primary mt-5 block w-full !border-b-0 py-3.5 text-center !text-on-primary no-underline">
              {trialCta(plan.trial_days) ?? `Subscribe · ${money(plan)}`}
            </Link>
            {trial && (
              <p className="m-0 mt-3 text-center font-body text-xs text-on-surface-variant">
                Card required. Cancel within the {trial} and you're never charged.
              </p>
            )}
          </>
        ) : (
          <p className="m-0 font-body text-[0.9375rem] text-on-surface-variant">
            {isLoading ? 'Loading pricing…' : 'Pricing is unavailable right now. Please try again shortly.'}
          </p>
        )}
      </div>

      <h2>Good to know</h2>
      <ul>
        <li>The price shown is what you pay. Any sales tax or VAT is already included.</li>
        <li>
          Cancel anytime from <strong>Account → Manage subscription</strong>; you keep access until the end of the
          period you've paid for.
        </li>
        <li>
          Purchases on paperbrain.dev are handled by our reseller and Merchant of Record, Paddle. Subscribing in the
          iPhone or Android app is billed by Apple or Google, so the store's price may differ.
        </li>
        <li>
          See the <Link to="/refunds">Refund Policy</Link> and <Link to="/terms">Terms</Link> for details.
        </li>
      </ul>
    </LegalLayout>
  );
};
